import React, { useState, useEffect } from 'react';
import { Card, Input, Form, message, Select, Tag } from 'antd';
import { Copy, ExternalLink, Send, Plus, Link as LinkIcon, Share2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/client';
import clsx from 'clsx';

export default function ReferralLinks() {
  const [links, setLinks] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchLinks();
    fetchChannels();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await api.get('/referral-links');
      setLinks(response.data);
    } catch (err) {
      message.error('Failed to load links');
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await api.get('/channels');
      setChannels(response.data);
    } catch (err) {
      console.error('Failed to load channels');
    }
  };

  const handleCreateLink = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/referral-links', {
        source_name: values.sourceName,
        channel_id: values.channelId,
      });
      setLinks([response.data, ...links]);
      form.resetFields();
      message.success('Referral link created!');
    } catch (err) {
      message.error('Error creating link');
    }
    setLoading(false);
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    message.success('Link copied!');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 mb-2">
            Реферальные ссылки
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Создавайте трекинговые ссылки для отслеживания эффективности рекламных кампаний и источников трафика.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Create Form */}
        <div className="lg:col-span-1">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-panel p-6 sticky top-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <Plus size={20} />
              </div>
              <h3 className="font-bold text-white text-lg">Новая ссылка</h3>
            </div>

            <Form form={form} layout="vertical" onFinish={handleCreateLink} className="space-y-4">
              <Form.Item
                label={<span className="text-slate-300">Название источника</span>}
                name="sourceName"
                rules={[{ required: true, message: 'Введите название' }]}
              >
                <Input className="input-field" placeholder="Например: Реклама в Facebook" />
              </Form.Item>
              
              <Form.Item
                label={<span className="text-slate-300">Целевой канал (Опционально)</span>}
                name="channelId"
                tooltip="Если выбрано, будет создана уникальная пригласительная ссылка Telegram."
              >
                <Select 
                  placeholder="Выберите канал" 
                  allowClear
                  className="custom-select w-full"
                  dropdownStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {channels.map(ch => (
                    <Select.Option key={ch.id} value={ch.id}>
                      <span className="text-white">{ch.channel_name}</span>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <button 
                type="submit" 
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 mt-4"
              >
                {loading ? 'Создание...' : 'Создать ссылку'}
              </button>
            </Form>
          </motion.div>
        </div>

        {/* Links List */}
        <div className="lg:col-span-2 space-y-4">
          <AnimatePresence mode="popLayout">
            {links.map((link, index) => (
              <motion.div
                key={link.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass-card p-5 group hover:border-primary/30 transition-all"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-3 mb-3">
                      <h4 className="font-bold text-white text-lg truncate">
                        {link.source_name}
                      </h4>
                      {link.telegram_invite_link && (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/20">
                          <Send size={10} />
                          TG Direct
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      {/* Web Link */}
                      <div className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/5 group-hover:border-white/10 transition-colors">
                        <div className="text-slate-400">
                          <LinkIcon size={14} />
                        </div>
                        <code className="text-sm text-primary-light flex-1 truncate font-mono">
                          {`${window.location.origin}/?ref=${link.unique_code}`}
                        </code>
                        <button
                          onClick={() => handleCopyLink(`${window.location.origin}/?ref=${link.unique_code}`)}
                          className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                        >
                          <Copy size={14} />
                        </button>
                      </div>

                      {/* Telegram Link */}
                      {link.telegram_invite_link && (
                        <div className="flex items-center gap-3 p-2 rounded-lg bg-blue-500/5 border border-blue-500/10 group-hover:border-blue-500/20 transition-colors">
                          <div className="text-blue-400">
                            <Send size={14} />
                          </div>
                          <code className="text-sm text-blue-300 flex-1 truncate font-mono">
                            {link.telegram_invite_link}
                          </code>
                          <button
                            onClick={() => handleCopyLink(link.telegram_invite_link)}
                            className="p-1.5 text-blue-400 hover:text-blue-200 hover:bg-blue-500/20 rounded transition-colors"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-1 pl-4 border-l border-white/10">
                    <div className="text-3xl font-bold text-white">
                      {link.clicks || 0}
                    </div>
                    <div className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                      Кликов
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {links.length === 0 && (
            <div className="py-20 text-center border-2 border-dashed border-white/10 rounded-2xl">
              <Share2 size={48} className="mx-auto mb-4 text-slate-600" />
              <h3 className="text-xl font-medium text-white mb-2">Нет ссылок</h3>
              <p className="text-slate-400">Создайте первую ссылку чтобы начать отслеживать трафик</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
